import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPortfolio, getFundName } from "../data/portfolios";
import { fmtCur } from "../utils/format";
import Logo from "../components/Logo";
import Tooltip from "../components/Tooltip";

const C = {
  bg: "#0a0a0a",
  card: "#111",
  border: "rgba(255,255,255,0.08)",
  gold: "#C9A84C",
  teal: "#00D4C8",
  violet: "#7C5CFC",
  white: "#F2F6FA",
  muted: "#888780",
  fog: "#6B7E91",
  red: "#FF3B5C",
  amber: "#E8C547",
  green: "#2EC8A0",
};

function signalMeta(s) {
  if (s === "red") return { label: "CRITICAL", color: C.red };
  if (s === "amber") return { label: "MONITOR", color: C.amber };
  return { label: "STABLE", color: C.green };
}

const PHRASES = [
  "Mapping the transformation gap...",
  "Benchmarking restructuring velocity...",
  "Quantifying the EBITDA opportunity...",
  "Surfacing leadership capability signals...",
];

const BADGES = [
  { src: "/badge_task.png", alt: "World's first Work Architecture Platform" },
  { src: "/badge_ai.png", alt: "World's first ASBC methodology" },
  { src: "/badge_4b.png", alt: "World's first 4B Talent Matrix" },
  { src: "/badge_board.png", alt: "World's first OCF role intelligence" },
  { src: "/badge_inclusion.png", alt: "World's first Inclusion by Architecture" },
];

function Counter({ target, duration = 1500, cur }) {
  const [val, setVal] = useState(0);
  const start = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const tick = (ts) => {
      if (!start.current) start.current = ts;
      const p = Math.min((ts - start.current) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 4);
      setVal(Math.floor(target * ease));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    start.current = null;
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return <>{fmtCur(val, cur)}</>;
}

function ScrollReveal({ direction = "left", children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const offset = direction === "left" ? "-60px" : "60px";
  return (
    <div ref={ref} style={{
      transform: visible ? "translateX(0)" : `translateX(${offset})`,
      opacity: visible ? 1 : 0,
      transition: "transform 0.5s ease-out, opacity 0.5s ease-out",
    }}>
      {children}
    </div>
  );
}

export default function FundLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const companies = useMemo(() => getPortfolio(slug), [slug]);
  const fundName = getFundName(slug);
  const cur = companies[0]?.currency || "USD";

  const totalEbitda = companies.reduce((s, c) => s + c.asbc_gap * 0.6, 0);
  const redCount = companies.filter((c) => c.signal === "red").length;
  const top3 = useMemo(() =>
    [...companies].sort((a, b) => b.asbc_gap * 0.6 - a.asbc_gap * 0.6).slice(0, 3),
    [companies]
  );

  const [phraseIdx, setPhraseIdx] = useState(0);
  const [showNumber, setShowNumber] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowNumber(true), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const iv = setInterval(() => setPhraseIdx((i) => (i + 1) % PHRASES.length), 3000);
    return () => clearInterval(iv);
  }, []);

  const handleEnter = () => {
    sessionStorage.setItem("fund_slug", slug);
    navigate("/dashboard");
  };

  if (!companies.length) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 36 }}>Fund not found</h2>
          <p style={{ color: C.muted, marginTop: 12 }}>The slug "{slug}" does not match any portfolio.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.white, fontFamily: "'Inter', sans-serif" }}>

      {/* ═══ HERO ═══ */}
      <section style={{
        display: "flex", flexDirection: "column",
        justifyContent: "center", alignItems: "center", textAlign: "center",
        padding: "80px 24px 32px", position: "relative",
      }}>
        {/* Logo top-left */}
        <div style={{ position: "absolute", top: 28, left: 32 }}>
          <Logo size="nav" />
          <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.gold, textTransform: "uppercase", letterSpacing: "0.18em", margin: "4px 0 0" }}>
            Portfolio Intelligence
          </p>
        </div>

        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, letterSpacing: "0.18em", color: C.gold, textTransform: "uppercase", marginBottom: 16, marginTop: 40 }}>
          Portfolio Intelligence · Exclusive Access
        </p>

        <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(32px, 6vw, 48px)", letterSpacing: "0.04em", color: C.white, margin: "0 0 16px" }}>
          {fundName.toUpperCase()}
        </h1>

        {/* Cycling phrase */}
        <p key={phraseIdx} style={{
          fontSize: 20, fontStyle: "italic", color: C.teal, margin: "0 0 40px",
          animation: "fund-fade-in 0.6s ease-out",
        }}>
          {PHRASES[phraseIdx]}
        </p>

        {/* EBITDA counter */}
        <div style={{
          opacity: showNumber ? 1 : 0,
          transform: showNumber ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 0.6s ease, transform 0.6s ease",
        }}>
          <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, letterSpacing: "0.14em", color: C.muted, textTransform: "uppercase", marginBottom: 12 }}>
            Total Portfolio EBITDA Opportunity <Tooltip text="Total recoverable margin improvement if each portfolio company closes its ASBC workforce transformation gap. Calculated as gap × 0.6, assuming 60% recoverability based on ASBC methodology." />
          </p>
          <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px, 8vw, 64px)", color: C.gold, margin: "0 0 12px", lineHeight: 1, letterSpacing: "0.02em" }}>
            {showNumber && <Counter target={totalEbitda} cur={cur} />}
          </p>
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>
            across {companies.length} portfolio companies · {redCount} require immediate attention <Tooltip text="Companies in CRITICAL signal have a restructuring velocity score below sector median and an ASBC gap exceeding 15% of estimated labour cost." />
          </p>
        </div>

        {/* Scroll indicator */}
        <div style={{ marginTop: 32 }}>
          <svg className="fund-chevron" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.fog} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </section>

      {/* ═══ SIGNAL CARDS ═══ */}
      <section style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 64px" }}>
        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, letterSpacing: "0.14em", color: C.gold, textTransform: "uppercase", marginBottom: 24, textAlign: "center", fontWeight: 600 }}>
          Where the gap is largest
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {top3.map((c, i) => {
            const meta = signalMeta(c.signal);
            const ebitda = c.asbc_gap * 0.6;
            return (
              <ScrollReveal key={c.id} direction={i % 2 === 0 ? "left" : "right"}>
                <div style={{
                  background: C.card, borderLeft: `3px solid ${C.gold}`,
                  borderRadius: "0 12px 12px 0", padding: "24px 28px",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <p style={{ fontSize: 18, fontWeight: 700, color: C.white, margin: 0 }}>{c.name}</p>
                      <p style={{ fontSize: 12, color: C.muted, margin: "4px 0 0" }}>{c.sector} · {c.headcount.toLocaleString()} employees</p>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", padding: "4px 10px",
                      borderRadius: 99, background: `${meta.color}14`, border: `1px solid ${meta.color}40`, color: meta.color,
                    }}>
                      {meta.label} <Tooltip text="CRITICAL signal: restructuring velocity significantly below sector peers. Immediate portfolio intervention recommended." />
                    </span>
                  </div>
                  <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 12, color: C.muted, margin: "14px 0 4px" }}>
                    EBITDA opportunity: <span style={{ color: C.gold, fontWeight: 700, fontSize: 20 }}>{fmtCur(ebitda, c.currency)}</span>
                  </p>
                  <p style={{ fontSize: 13, fontStyle: "italic", color: "rgba(255,255,255,0.65)", margin: "8px 0 0", lineHeight: 1.5 }}>
                    {c.urgency_reason}
                  </p>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ maxWidth: 600, margin: "0 auto", padding: "0 24px 64px", textAlign: "center" }}>
        <button
          onClick={handleEnter}
          style={{
            width: "100%", padding: "18px 32px", fontSize: 16, fontWeight: 700,
            background: C.gold, color: C.bg, border: "none", borderRadius: 10,
            cursor: "pointer", letterSpacing: "0.02em",
            boxShadow: `0 0 32px ${C.gold}40`,
          }}
        >
          View Full Portfolio Intelligence →
        </button>
        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 11, color: C.muted, marginTop: 16, letterSpacing: "0.04em" }}>
          6 screens · Signal Room access · Target Due Diligence · Leadership Scorecard
        </p>
        <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 10, color: C.fog, marginTop: 8, letterSpacing: "0.04em" }}>
          Built in Dubai, UAE · reAImagination · Shim Partners
        </p>
      </section>

      {/* ═══ DEBU QUOTE ═══ */}
      <section style={{ maxWidth: 600, margin: "0 auto", padding: "0 24px 48px" }}>
        <div style={{ borderLeft: `3px solid ${C.gold}`, paddingLeft: 24 }}>
          <p style={{ fontSize: 16, fontStyle: "italic", color: "rgba(255,255,255,0.85)", lineHeight: 1.65, margin: "0 0 16px" }}>
            "Most organisations price roles. We price tasks. When you price at task level across six workforce types, the gap between what you spend and what is optimal becomes visible for the first time."
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/debu-mishra.jpeg"
              alt="Debabrat Mishra"
              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
              style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `2px solid ${C.gold}` }}
            />
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.gold, display: "none", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: C.bg }}>DM</span>
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: C.white, margin: 0 }}>Debabrat Mishra</p>
              <p style={{ fontSize: 11, color: C.muted, margin: "2px 0 0" }}>Founder, reAImagination · Shim Partners</p>
              <p style={{ fontFamily: "'Overpass Mono', monospace", fontSize: 9, color: C.teal, letterSpacing: "0.06em", margin: "2px 0 0" }}>
                Arthur Andersen · PwC · Deloitte · Hay Group · Aon Hewitt · 35 years
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ BADGES ═══ */}
      <section style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px 80px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", alignItems: "center" }}>
          {BADGES.map((b) => (
            <img key={b.alt} src={b.src} alt={b.alt} style={{ height: 80, width: "auto" }} />
          ))}
        </div>
      </section>

      <style>{`
        @keyframes fund-fade-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fund-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(6px)}}
        .fund-chevron{animation:fund-bounce 2s ease-in-out infinite}
      `}</style>
    </div>
  );
}
